import {useLingui} from "@lingui/react";
import {useAxios} from "../../utils/hooks";
import React, {useEffect, useRef, useState} from "react";
import {AxiosResponse} from "axios";
import moment from "moment";
import {openNotification} from "../../utils/openNotification";
import {Button, Card, DatePicker, Divider, Form, Input, Radio, Space, Tooltip, Upload, UploadProps} from "antd";
import {FolderOpenOutlined, InfoCircleTwoTone, ProfileOutlined, UploadOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import FileDownload from "js-file-download";
import useSafeState from "../../utils/safe-state";

interface Interface {
    data?: any;
    form?: any;
}

const OnboardingFirstStep = (props: Interface) => {

    const data = props.data || {};
    const form = props.form;

    const {i18n} = useLingui();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [collectInfoValue, setCollectInfoValue] = useState<number | undefined>(undefined);
    const [riskAssessmentValue, setRiskAssessmentValue] = useState<number | undefined>(undefined);
    const [itemsFileList, setItemsFileList] = useSafeState<Array<any>>([]);
    const [itemsLoading, setItemsLoading] = useSafeState(false);
    const getDetailedErrorMessage = (error: any): string => {
        let errorMessage = 'An unexpected error occurred. Please try again.';

        if (error.response) {
            if (error.response.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
            }

            if (errorMessage === 'An unexpected error occurred. Please try again.') {
                if (error.response.status === 400) {
                    errorMessage = 'Invalid file format. Please check:\n' +
                        '• Headers must be: LOCATION NAME, LOCATION GEOJSON\n' +
                        '• Only MULTIPOLYGON format is accepted\n' +
                        '• All rows must have both name and polygon data\n' +
                        '• Format: MULTIPOLYGON (((lng lat, lng lat, lng lat, lng lat)))';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error while processing your file. Please verify:\n' +
                        '• File is not corrupted\n' +
                        '• Polygon coordinates are valid\n' +
                        '• No special characters in location names';
                }
            }
        } else if (error.request) {
            errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
        } else if (error.message) {
            errorMessage = error.message;
        }

        return errorMessage;
    };


    useEffect(() => {
        getProfileData();
    }, []);



    const getProfileData = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/onboarding-data/get-first-step-data', {})
                .then(function (response : AxiosResponse<any>) {

                    const data = response.data;
                    console.log(data);

                    if (data.sme !== undefined && data.sme !== null) {
                        data.sme = Number(data.sme);
                    }

                    if (data.collectInfo !== undefined && data.collectInfo !== null) {
                        data.collectInfo = Number(data.collectInfo);
                        setCollectInfoValue(data.collectInfo);
                    }

                    if (data.riskAssessment !== undefined && data.riskAssessment !== null) {
                        data.riskAssessment = Number(data.riskAssessment);
                        setRiskAssessmentValue(data.riskAssessment);
                    }


                    const profileValues = {
                        sme: data.sme,
                        collectInfo: data.collectInfo,
                        collectInfoDeadline: data.collectInfoDeadline ? moment(data.collectInfoDeadline) : null,
                        riskAssessment: data.riskAssessment,
                        riskAssessmentDescription: data.riskAssessmentDescription,
                        riskAssessmentDeadline: data.riskAssessmentDeadline ? moment(data.riskAssessmentDeadline) : null,
                    };

                    form.setFieldsValue({
                        onboarding: {
                            profile: profileValues
                        }
                    });
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                });
        }
    };

    const handleDownloadFile = (): void => {
        if (axiosInstance.current != null) {
            const FileDownload = require('js-file-download');
            axiosInstance
                .current({
                    url: process.env.REACT_APP_API_BASE_URL + '/public/file/get?folder=' + 'eudrdocuments' + '&filename=onboarding/EUDR_ITEMS_TEMPLATE.xlsx',
                    method: 'GET',
                    responseType: 'blob',
                })
                .then(response => {
                    FileDownload(response.data, 'EUDR_ITEMS_TEMPLATE.xlsx');
                });
        }
    };

    const itemsProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/items/upload-giga-xls',
        multiple: false,
        fileList: itemsFileList,
        accept: '.xlsx',
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = itemsFileList;
            fileList2.push(file);
            setItemsFileList(fileList2);
            return false;
        },
    };

    const handleUploadItemsFile = () => {

        const fileList = itemsFileList
        if (!fileList || fileList.length === 0) {
            openNotification(
                'error',
                'No file selected. Please select a file to upload.'
            );
            return;
        }

        if (axiosInstance.current != null) {
            setItemsLoading(true);

            const formData = new FormData();

            itemsFileList.forEach(file => { formData.append('file', file); });

            axiosInstance.current
                .post('/resource/items/upload-giga-xls', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() => {
                    openNotification('customSuccess', 'File successfully uploaded');
                })
                .catch(function (error) {
                    const errorMessage = getDetailedErrorMessage(error);
                    openNotification('error', errorMessage);
                })
                .finally(() => {
                    setItemsFileList([]);
                    setItemsLoading(false);
                });
        }
    };

    return (
        <Form form={form} component={false}>
            <Card
                title={<><ProfileOutlined style={{marginRight: 10}}/>Your Business Profile (1/2)</>}
                bordered={true}
                style={{width: 800, border: '1px solid #39893e'}}
                headStyle={{backgroundColor: 'darkseagreen'}}
                bodyStyle={{backgroundColor: '#e1efe1'}}
            >

                <b>1. Does your organisation qualified as an SME?</b>
                <Tooltip
                    placement="top"
                    title={
                        <>
                            <div>
                                As per the EUDR regulation: 'SME' means micro, small and medium-sized undertakings
                                as defined in Article 3 of Directive 2013/34/EU of the European Parliament and of
                                the Council
                            </div>
                        </>
                    }
                    children={
                        <InfoCircleTwoTone
                            twoToneColor="#1890ff"
                            style={{marginLeft: 5}}
                        />
                    }
                />
                <a href="https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:02013L0034-20240528#art_3"
                   target="_blank"> About SME</a>
                <Form.Item
                    style={{marginTop: 10}}
                    name={["onboarding", "profile", "sme"]}
                    rules={[{required: true, message: 'Please select an option'}]}>

                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}>Yes</Radio>
                            <Radio value={2}>No</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider></Divider>

                <b>2. Did you collect necessary information from your suppliers in line with EUDR (EU)
                    2023/1115?</b>
                <Form.Item
                    style={{marginTop: 10}}
                    name={["onboarding", "profile", "collectInfo"]}
                    rules={[{required: true, message: 'Please select an option'}]}>

                    <Radio.Group onChange={(e) => {
                        setCollectInfoValue(e.target.value);
                        if (e.target.value !== 2) {
                            form.setFieldsValue({
                                onboarding: {
                                    profile: {
                                        collectInfoDeadline: null
                                    }
                                }
                            });
                        }
                        form.validateFields([
                            ["onboarding", "profile", "collectInfoDeadline"]
                        ]);
                    }}>
                        <Space direction="horizontal">
                            <Radio value={1}>Yes</Radio>
                            <Radio value={2}>No</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <span>If NO, please indicate target date to collect necessary information.</span>
                <Form.Item
                    name={["onboarding", "profile", "collectInfoDeadline"]}
                    rules={[{required: collectInfoValue === 2, message: 'Please select a target date'}]}
                >
                    <DatePicker style={{marginTop: 10}} disabled={collectInfoValue !== 2}/>
                </Form.Item>
                <Divider></Divider>

                <b>3. Did you perform risk assessment in line with EUDR (EU) 2023/1115?</b>
                <Form.Item
                    style={{marginTop: 10}}
                    name={["onboarding", "profile", "riskAssessment"]}
                    rules={[{required: true, message: 'Please select an option'}]}>

                    <Radio.Group
                        onChange={(e) => {
                            setRiskAssessmentValue(e.target.value);
                            if (e.target.value !== 3) {
                                form.setFieldsValue({
                                    onboarding: {
                                        profile: {
                                            riskAssessmentDescription: null,
                                            riskAssessmentDeadline: null
                                        }
                                    }
                                });
                            }
                            form.validateFields([
                                ["onboarding", "profile", "riskAssessmentDescription"],
                                ["onboarding", "profile", "riskAssessmentDeadline"]
                            ]);
                        }}>
                        <Space direction="horizontal">
                            <Radio value={1}>No, or negligible risk identified</Radio>
                            <Radio value={2}>Identified risk is higher than negligible</Radio>
                            <Radio value={3}>Did not perform risk assessment yet</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <span>What was the result of the risk assessment?</span>
                <Form.Item
                    style={{marginBottom: 10}}
                    name={["onboarding", "profile", "riskAssessmentDescription"]}
                    rules={[{
                        required: riskAssessmentValue === 2,
                        message: 'Please provide risk assessment result'
                    }]}
                >
                    <TextArea rows={4} placeholder="Please write the risk assesment here..."
                              disabled={riskAssessmentValue === 3}/>
                </Form.Item>
                <span>If you did not perform yet, plase indicate target date for completing your risk assessment.</span>
                <Form.Item
                    style={{marginBottom: 10}}
                    name={["onboarding", "profile", "riskAssessmentDeadline"]}
                    rules={[{
                        required: riskAssessmentValue === 3,
                        message: 'Please select a target date'
                    }]}
                >
                    <DatePicker style={{marginTop: 10}} disabled={riskAssessmentValue !== 3}/>
                </Form.Item>
                <Divider></Divider>
                <Form.Item style={{marginTop: 10}}>
                    <p><b>4. Please upload your items.
                        <i> (Please use<a
                            onClick={() => handleDownloadFile()}> THIS </a>template
                            to create a
                            file.)</i></b>
                    </p>
                    <div style={{marginBottom: 10}}>
                        <Upload {...itemsProps}>
                            <Button
                                style={{marginTop: 10}}
                                icon={<FolderOpenOutlined/>}
                            > Select File</Button>
                        </Upload>
                    </div>
                    <div className="center-card">
                        <Button loading={itemsLoading} htmlType="button" type="primary"
                                onClick={() => handleUploadItemsFile()}>
                            <ButtonWithIcon
                                icon={<UploadOutlined/>}
                                text={<span>Upload</span>}
                            />
                        </Button>
                    </div>
                </Form.Item>
            </Card>
        </Form>
    );
};

export default OnboardingFirstStep;